import { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Briefcase, Loader2, RefreshCw, ShieldX } from "lucide-react";
import { api, formatApiError } from "../api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";

// Owner-only diligence snapshot — aggregates only, never personal data.
const fmtTs = (iso) => (iso ? new Date(iso).toLocaleString("pl-PL", { timeZone: "Europe/Warsaw" }) : "—");
const SOURCE_LABELS = {
  linkedin: "LinkedIn", pink: "PINK", other_social: "Inne social media",
  direct: "Bezpośredni", search: "Wyszukiwarki", other: "Inne",
};
const STAGE_LABELS = {
  visit: "Sesje aktywne", search: "Szukali działki", parcel_view: "Wyświetlili działkę",
  preview: "Podgląd raportu", login_prompt: "Zachęta do logowania", register: "Rejestracja",
  save: "Zapis działki", watch: "Planning Watch",
};
const ORG_LABELS = {
  samorzad: "Samorząd", deweloper: "Deweloper", kancelaria: "Kancelaria",
  posrednik: "Pośrednik", inwestor: "Inwestor", urbanista: "Urbanista", inne: "Inne",
};
const CONV_LABELS = {
  register: "Rejestracje", parcel_save: "Zapisy działek",
  watch_start: "Starty Planning Watch", planning_watch_activated: "Aktywacje nadzoru",
};
const DROPOFF_LABELS = {
  report_preview: "podgląd raportu", save_cta_view: "wyświetlenie CTA „Zapisz działkę”",
  save_cta_click: "kliknięcie „Zapisz działkę”", reg_start: "start rejestracji",
  reg_complete: "ukończenie rejestracji", post_reg_save: "zapis działki po rejestracji",
  watch_activated: "aktywacja Planning Watch",
};
const PARTNER_LABELS = {
  unique_sessions_30d: "Unikalne sesje (30 dni)",
  registrations_30d: "Rejestracje (30 dni)",
  activated_users_saves_30d: "Aktywowani użytkownicy — zapisy (30 dni)",
  watch_activations_30d: "Aktywacje Planning Watch (30 dni)",
  institutional_leads_total: "Leady instytucjonalne (łącznie)",
  pwa_installs_30d: "Instalacje PWA (30 dni)",
  paid_conversion: "Konwersja płatna",
  conversion_trend_wow: "Trend sesji (tydzień do tygodnia)",
  data_source_availability_live: "Dostępność źródeł danych (live, 0–3)",
  adapter_errors_30d: "Błędy adapterów (30 dni)",
};

const Section = ({ title, testId, children }) => (
  <section data-testid={testId} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
    <h2 className="font-heading text-base font-bold text-slate-900">{title}</h2>
    {children}
  </section>
);

const Kpi = ({ label, value, testId }) => (
  <div className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
    <p data-testid={testId} className="mt-1 font-heading text-2xl font-extrabold text-slate-900">
      {(value ?? 0).toLocaleString("pl-PL")}
    </p>
  </div>
);

export default function BusinessReadinessPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get("/admin/business-readiness");
      setData(r.data);
    } catch (e) {
      setError(e);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (user?.role === "admin") load();
  }, [user, load]);

  return (
    <div data-testid="business-readiness-page" className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
      <Helmet>
        <title>Gotowość biznesowa — PlanGmina (owner)</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">
            <Briefcase className="text-[#DC143C]" size={26} /> Gotowość biznesowa
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Migawka transakcyjna dla właściciela — wyłącznie agregaty, bez danych osobowych.{" "}
            <span data-testid="br-timezone" className="font-medium">Strefa czasowa: Europe/Warsaw</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="br-analytics-link" variant="outline" size="sm" onClick={() => navigate("/admin/analytics")}>
            Analityka
          </Button>
          <Button data-testid="br-refresh" variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw size={14} className={loading ? "mr-1.5 animate-spin" : "mr-1.5"} /> Odśwież
          </Button>
        </div>
      </header>

      {!user && (
        <div data-testid="br-login-gate" className="mt-8 rounded-xl border border-slate-200 bg-slate-50 p-8 text-center">
          <p className="text-sm text-slate-600">Ta strona wymaga konta administratora.</p>
          <Button data-testid="br-login-btn" className="mt-4 bg-[#DC143C] text-white hover:bg-[#B91C1C]" onClick={() => navigate("/logowanie?returnTo=/admin/business")}>
            Zaloguj
          </Button>
        </div>
      )}
      {user && user.role !== "admin" && (
        <div data-testid="br-forbidden" className="mt-8 flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-6">
          <ShieldX className="text-red-500" size={20} />
          <p className="text-sm font-medium text-red-700">Brak dostępu — panel właściciela wymaga administratora po MFA.</p>
        </div>
      )}
      {loading && (
        <div data-testid="br-loading" className="flex items-center justify-center gap-2 py-20 text-sm text-slate-500">
          <Loader2 size={18} className="animate-spin" /> Ładowanie…
        </div>
      )}
      {error && (
        <div data-testid="br-error" className="mt-6 flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-6">
          <AlertTriangle className="text-red-500" size={20} />
          <p className="text-sm text-red-700">{formatApiError(error)}</p>
        </div>
      )}

      {user?.role === "admin" && data && (
        <div className="mt-6 space-y-5">
          <p data-testid="br-generated" className="text-xs text-slate-400">
            Wygenerowano: {data.generated_at_local} · uptime procesu: {Math.round((data.uptime?.process_uptime_seconds ?? 0) / 60)} min
          </p>

          <Section title="Sesje (DAU / WAU / MAU — agregaty)" testId="br-sessions">
            <div className="mt-4 grid grid-cols-3 gap-3">
              <Kpi label="Dziś" value={data.sessions?.day} testId="br-sessions-day" />
              <Kpi label="7 dni" value={data.sessions?.week} testId="br-sessions-week" />
              <Kpi label="30 dni" value={data.sessions?.month} testId="br-sessions-month" />
            </div>
          </Section>

          <Section title="Kluczowe metryki — 30 dni" testId="br-kpis">
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Kpi label="Wizyty / sesje rozpoczęte" value={data.traffic?.started_sessions} testId="br-visits" />
              <Kpi label="Rejestracje" value={data.totals_30d?.registrations} testId="br-registrations" />
              <Kpi label="Zapisane działki (łącznie)" value={data.saved_parcels_total} testId="br-saved-total" />
              <Kpi label="Aktywacje nadzoru" value={data.totals_30d?.planning_watch_activated} testId="br-watch-30d" />
              <Kpi label="Podglądy raportów" value={data.totals_30d?.report_previews} testId="br-previews" />
              <Kpi label="Leady instytucjonalne (łącznie)" value={data.institutional?.leads_total} testId="br-leads-total" />
              <Kpi label="Instalacje PWA" value={data.totals_30d?.pwa_installs} testId="br-pwa-30d" />
              <Kpi label="Błędy adapterów" value={data.health?.adapter_errors_30d} testId="br-adapter-errors" />
            </div>
          </Section>

          <Section title="Podatki i płatności — bramka live (fail-closed)" testId="br-tax-live-gate">
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span data-testid="br-tax-live-ready" className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold ${
                data.tax?.live_ready ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                {data.tax?.live_ready ? "GOTOWE DO PŁATNOŚCI LIVE" : "PŁATNOŚCI LIVE: ZABLOKOWANE"}
              </span>
              <span className="text-xs text-slate-500">
                tryb dostawcy: TEST · wersja reguł: {data.tax?.tax_rule_version || "—"}
              </span>
            </div>
            <ul className="mt-3 space-y-1 text-xs text-slate-600">
              {(data.tax?.blocked_reasons || []).map((r) => (
                <li key={r} data-testid="br-tax-blocked-reason" className="flex gap-2">
                  <span className="text-amber-600">•</span>{r}
                </li>
              ))}
              {(data.tax?.blocked_reasons || []).length === 0 && (
                <li className="text-emerald-700">Brak blokad — bramka otwarta.</li>
              )}
            </ul>
            <p data-testid="br-tax-config-state" className="mt-3 text-xs text-slate-600">
              Podmiot sprzedawcy potwierdzony: <strong>{data.tax?.merchant_entity_confirmed ? "tak" : "nie"}</strong>
              {" · "}stawki VAT potwierdzone: <strong>{data.tax?.vat_rates_confirmed ? "tak" : "nie"}</strong>
              {" · "}OSS: <strong>{data.tax?.oss_status || "—"}</strong>
              {" · "}przegląd podatkowy: <strong>{data.tax?.professional_review_status || "—"}</strong>
            </p>
            <p className="mt-2 text-[11px] text-slate-400">
              Granica kontrolna: PlanGmina sprzedaje produkt, Billing przetwarza zdarzenie handlowe,
              Hussar Accounting decyduje o księgowaniu — REVIEW_REQUIRED/BLOCKED blokują produkcję (fail-closed).
            </p>
          </Section>

          <Section title="Jakość ruchu — Traffic Integrity (30 dni)" testId="br-traffic-integrity">
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Kpi label="Sesje rozpoczęte (raw)" value={data.traffic?.started_sessions} testId="br-traffic-raw" />
              <Kpi label="Sesje aktywne (raw)" value={data.traffic?.raw_sessions} testId="br-traffic-raw-sessions" />
              <Kpi label="Sesje ludzkie (kwalifikowane)" value={data.traffic?.qualified_sessions} testId="br-traffic-qualified" />
              <Kpi label="Sesje zaangażowane w produkt" value={data.traffic?.engaged_sessions} testId="br-traffic-engaged" />
              <Kpi label="Ruch automat./techniczny"
                   value={["bot_known", "link_preview", "search_crawler", "security_scanner", "monitoring_probe", "unknown_technical"]
                     .reduce((a, c) => a + (data.traffic?.by_class?.[c] ?? 0), 0)}
                   testId="br-traffic-automated" />
            </div>
            <p className="mt-3 text-xs text-slate-500" data-testid="br-traffic-qualified-src">
              Sesje ludzkie wg źródła:{" "}
              {Object.entries(data.traffic?.qualified_by_source || {}).map(([c, n]) => `${c}: ${n}`).join(" · ") || "—"}
            </p>
            <p className="mt-1 text-xs text-slate-500" data-testid="br-traffic-engaged-src">
              Sesje zaangażowane wg źródła:{" "}
              {Object.entries(data.traffic?.engaged_by_source || {}).map(([c, n]) => `${c}: ${n}`).join(" · ") || "—"}
            </p>
            <p className="mt-3 text-[11px] text-slate-400" data-testid="br-traffic-methodology">
              Model trzech warstw: Sesje surowe → Sesje ludzkie (kwalifikowane) → Sesje zaangażowane w produkt.
              Metodologia: sesje ludzkie wymagają sygnału zaangażowania przeglądarki po pobraniu strony
              (≥5 s widoczności, scroll, kliknięcie, nawigacja lub akcja produktowa). Sesja zaangażowana w produkt
              to sesja ludzka z co najmniej jedną sensowną akcją produktową (szukanie/podgląd działki, podgląd
              raportu, rejestracja, zapis, Planning Watch, otwarcie CTA instytucjonalnego, instalacja PWA) —
              same wyświetlenia CTA ani zachęta do logowania nie liczą się. Znany ruch automatyczny,
              podglądy linków, roboty wyszukiwarek, sondy monitoringu i ruch techniczny są wykluczone z metryk
              kwalifikowanych. Surowe liczniki pozostają widoczne i oznaczone jako raw. Pomiar sesji ludzkich
              jest dokładny od {data.traffic?.measurement_start}, zaangażowanych od{" "}
              {data.traffic?.engaged_measurement_start}; danych historycznych nie przepisujemy.
              Sesje rozpoczęte (ping bezpośredni lub wejście odtworzone z pierwszej wiarygodnej
              aktywności) liczymy wg nowej metodologii od {data.traffic?.started_measurement_start}.
            </p>
          </Section>

          <div className="grid gap-5 lg:grid-cols-2">
            <Section title="Lejek konwersji — unikalne sesje (30 dni, SUROWE)" testId="br-funnel">
              <div className="mt-4 space-y-2">
                {Object.entries(STAGE_LABELS).map(([stage, label]) => {
                  const n = data.funnel_30d?.[stage] ?? 0;
                  const max = data.funnel_30d?.visit || 1;
                  return (
                    <div key={stage} data-testid={`br-funnel-${stage}`} className="flex items-center gap-3">
                      <span className="w-40 shrink-0 text-xs font-medium text-slate-600">{label}</span>
                      <div className="h-5 flex-1 overflow-hidden rounded-md bg-slate-100">
                        <div className="h-full rounded-md bg-slate-700" style={{ width: `${Math.max((n / max) * 100, n ? 2 : 0)}%` }} />
                      </div>
                      <span className="w-14 text-right font-mono-data text-xs font-bold text-slate-900">{n.toLocaleString("pl-PL")}</span>
                    </div>
                  );
                })}
              </div>
            </Section>

            <Section title="Miks źródeł ruchu (30 dni)" testId="br-source-mix">
              <div className="mt-4 flex flex-wrap gap-2">
                {Object.entries(SOURCE_LABELS).map(([k, l]) => (
                  <span key={k} data-testid={`br-source-${k}`} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-700">
                    {l}: <strong>{(data.source_mix_30d?.[k] ?? 0).toLocaleString("pl-PL")}</strong>
                  </span>
                ))}
              </div>
              <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-slate-500">Konwersje wg źródła (30 dni)</p>
              <div className="mt-2 space-y-1.5" data-testid="br-conversions">
                {Object.entries(CONV_LABELS).map(([e, label]) => (
                  <p key={e} className="text-xs text-slate-600">
                    <span className="font-medium text-slate-800">{label}:</span>{" "}
                    {(data.conversions_30d?.[e]?.total ?? 0).toLocaleString("pl-PL")}
                    {" · "}
                    {Object.keys(SOURCE_LABELS)
                      .filter((c) => data.conversions_30d?.[e]?.by_source?.[c])
                      .map((c) => `${SOURCE_LABELS[c]} ${data.conversions_30d[e].by_source[c]}`)
                      .join(" · ") || "brak atrybucji"}
                  </p>
                ))}
              </div>
            </Section>
          </div>

          <Section title="Leady instytucjonalne" testId="br-institutional">
            <div className="mt-4 flex flex-wrap gap-2">
              {Object.entries(ORG_LABELS).map(([k, l]) => (
                <span key={k} data-testid={`br-inst-org-${k}`} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-700">
                  {l}: <strong>{(data.institutional?.by_org_type?.[k] ?? 0).toLocaleString("pl-PL")}</strong>
                </span>
              ))}
            </div>
            <p className="mt-3 text-xs text-slate-500">
              30 dni: wyświetlenia CTA <strong>{data.institutional?.cta_views_30d ?? 0}</strong> · prośby o prezentację <strong>{data.institutional?.requests_30d ?? 0}</strong>
            </p>
          </Section>

          <div className="grid gap-5 lg:grid-cols-2">
            <Section title="Zdrowie źródeł danych (na żywo)" testId="br-health">
              <div className="mt-3 space-y-1.5 text-xs text-slate-600">
                {["uldk", "mpzp_wms", "pog_wms"].map((k) => (
                  <p key={k} data-testid={`br-health-${k}`}>
                    <span className={`font-semibold ${data.health?.[k]?.ok ? "text-emerald-700" : "text-amber-700"}`}>
                      {data.health?.[k]?.ok ? "OK" : "PROBLEM"}
                    </span>{" "}
                    {k.toUpperCase()} · {data.health?.[k]?.latency_ms ?? "—"} ms
                  </p>
                ))}
                <p data-testid="br-source-health" className="pt-1">
                  Gminy: wszystkie źródła OK <strong>{data.health?.source_health?.all_ok ?? 0}</strong> · częściowo niedostępne <strong>{data.health?.source_health?.degraded ?? 0}</strong> · niedostępne <strong>{data.health?.source_health?.failed ?? 0}</strong>
                </p>
              </div>
            </Section>

            <Section title="Zadania cykliczne" testId="br-crons">
              <div className="mt-3 space-y-1.5 text-xs text-slate-600">
                {Object.entries(data.crons || {}).map(([job, c]) => (
                  <p key={job} data-testid={`br-cron-${job}`}>
                    <span className="font-medium text-slate-800">{job}</span> · ostatni: {c.last_run_local || fmtTs(c.last_run)} · {c.last_status || "—"}
                  </p>
                ))}
                {Object.keys(data.crons || {}).length === 0 && <p>—</p>}
              </div>
            </Section>
          </div>

          <Section title="Aktywacja — wskaźniki (30 dni)" testId="br-activation">
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-5">
              {[
                ["Konwersja rejestracji", data.activation?.registration_conversion, "br-rate-register"],
                ["Aktywacja zapisu działki", data.activation?.parcel_save_activation, "br-rate-save"],
                ["Aktywacja Planning Watch", data.activation?.watch_activation, "br-rate-watch"],
                ["Podgląd raportu → zapis", data.activation?.preview_to_save, "br-rate-preview-save"],
                ["Wskaźnik powrotów", data.activation?.returning_user_rate, "br-rate-returning"],
              ].map(([label, val, tid]) => (
                <div key={tid} className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
                  <p data-testid={tid} className="mt-1 font-heading text-2xl font-extrabold text-slate-900">
                    {val === null || val === undefined ? "—" : `${(val * 100).toFixed(1)}%`}
                  </p>
                </div>
              ))}
            </div>
          </Section>

          <Section title="Gdzie tracimy użytkowników?" testId="br-dropoff">
            {data.dropoff ? (
              <p data-testid="br-dropoff-verdict" className="mt-2 text-sm text-slate-700">
                Największy spadek: <strong>{DROPOFF_LABELS[data.dropoff.from] || data.dropoff.from} → {DROPOFF_LABELS[data.dropoff.to] || data.dropoff.to}</strong>
                {" "}(−{Math.round(data.dropoff.loss * 100)}%, {data.dropoff.from_count} → {data.dropoff.to_count} unikalnych sesji, 30 dni)
              </p>
            ) : (
              <p data-testid="br-dropoff-verdict" className="mt-2 text-sm text-slate-500">Za mało danych, aby wskazać spadek.</p>
            )}
            <p className="mt-2 text-[11px] text-slate-400">
              Diagnoza wyłącznie z zagregowanych liczników — bez twierdzeń o przyczynach.
            </p>
          </Section>

          <Section title="Dowody aktywacji (pierwsze wystąpienia + liczniki)" testId="br-activation-evidence">
            <div className="mt-3 space-y-1.5 text-xs text-slate-600">
              {[
                ["first_registration", "Pierwsza rejestracja"],
                ["first_saved_parcel", "Pierwsza zapisana działka"],
                ["first_watch_activation", "Pierwsza aktywacja Planning Watch"],
                ["first_institutional_lead", "Pierwszy lead instytucjonalny"],
                ["first_full_report", "Pierwszy pełny raport"],
              ].map(([k, label]) => (
                <p key={k} data-testid={`br-evidence-${k}`}>
                  <span className="font-medium text-slate-800">{label}:</span>{" "}
                  {data.activation_evidence?.[k] || "—"}
                </p>
              ))}
              <p className="pt-1">
                30 dni: rejestracje <strong>{data.activation_evidence?.counts_30d?.registrations ?? 0}</strong> · zapisy <strong>{data.activation_evidence?.counts_30d?.saved_parcels ?? 0}</strong> · aktywacje nadzoru <strong>{data.activation_evidence?.counts_30d?.watch_activations ?? 0}</strong> · leady <strong>{data.activation_evidence?.counts_30d?.institutional_leads ?? 0}</strong> · pełne raporty <strong>{data.activation_evidence?.counts_30d?.full_reports ?? 0}</strong>
              </p>
            </div>
            <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Rejestr kamieni milowych (append-only)</p>
            <div data-testid="br-milestones" className="mt-2 space-y-1 text-xs text-slate-600">
              {(data.milestones || []).map((m) => (
                <p key={m.key}><span className="font-mono-data text-slate-800">{m.key}</span> — {fmtTs(m.first_at)}</p>
              ))}
              {(data.milestones || []).length === 0 && <p>—</p>}
            </div>
          </Section>

          <Section title="Partner / M&A Readiness" testId="br-partner-readiness">
            <p className="mt-1 text-xs text-slate-400">
              Wyłącznie zagregowane dowody operacyjne. Status: MEASURED = wystarczające dane · INSUFFICIENT DATA = sygnał poniżej progu · NOT YET PROVEN = brak. Bez wyceny ani wyniku transakcyjnego.
            </p>
            <div className="mt-4 overflow-x-auto">
              <table data-testid="br-partner-table" className="w-full text-xs">
                <thead>
                  <tr className="text-left text-slate-400">
                    <th className="py-1.5 pr-3 font-semibold">Metryka</th>
                    <th className="px-2 py-1.5 text-right font-semibold">Wartość</th>
                    <th className="px-2 py-1.5 text-right font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(data.partner_readiness || []).map((m) => (
                    <tr key={m.key} data-testid={`br-partner-${m.key}`}>
                      <td className="py-1.5 pr-3 font-medium text-slate-600">{PARTNER_LABELS[m.key] || m.key}</td>
                      <td className="px-2 py-1.5 text-right font-mono-data text-slate-900">
                        {typeof m.value === "number" ? m.value.toLocaleString("pl-PL") : m.value}
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
                          m.status === "MEASURED" ? "bg-emerald-50 text-emerald-700"
                          : m.status === "INSUFFICIENT DATA" ? "bg-amber-50 text-amber-700"
                          : "bg-slate-100 text-slate-500"
                        }`}>{m.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          <Section title="Postawa prywatności (deklarowana, egzekwowana testami)" testId="br-privacy">
            <ul className="mt-3 space-y-1 text-xs text-slate-600">
              <li>Model analityki: {data.privacy?.analytics_model}</li>
              <li>Surowe adresy URL odsyłające: {data.privacy?.raw_referrer_urls_stored ? "TAK" : "nigdy"}</li>
              <li>Surowe adresy IP: {data.privacy?.raw_ip_storage ? "TAK" : "nigdy"} (auth-audit: {data.privacy?.auth_audit_ip})</li>
              <li>MFA administratora: {data.privacy?.admin_mfa ? "wymagane" : "—"} · Skanowanie uploadów: {data.privacy?.upload_scanning}</li>
              <li>Sesje łączone z kontami: {data.privacy?.sessions_joined_to_accounts ? "TAK" : "nigdy"}</li>
            </ul>
          </Section>

          <Section title="Inwentarz funkcji" testId="br-features">
            <ul className="mt-3 grid gap-1 text-xs text-slate-600 sm:grid-cols-2">
              {(data.features || []).map((f) => (
                <li key={f} className="flex gap-2"><span className="text-emerald-600">✓</span>{f}</li>
              ))}
            </ul>
          </Section>
        </div>
      )}
    </div>
  );
}
